import type { Participant } from "@/api/types";
import { formatDateShort, relativeTime } from "@/lib/format-date";

const EDGE_PARTICIPANT_ID_PATTERN = /^edge\d{3}$/i;

export const PERIODIC_GAP_SOURCES = [
  "ALIVE",
  "BLUETOOTH_SCAN",
  "PHYSICAL_ACTIVITY",
  "WIFI_EVENT",
] as const;

export interface UploadAlertFilterOptions {
  includeAllParticipants?: boolean;
}

export function isEdgeParticipantId(identifier: string): boolean {
  return EDGE_PARTICIPANT_ID_PATTERN.test(identifier.trim());
}

export function isEdgeParticipant(participant: Participant): boolean {
  return (
    isEdgeParticipantId(participant.username || "") ||
    isEdgeParticipantId(participant.user_id || "")
  );
}

export function hasUploadAlert(
  participant: Participant,
  options?: UploadAlertFilterOptions | number,
): boolean {
  if ((participant.total_uploads ?? 0) < 1) {
    return false;
  }
  const filterOptions =
    typeof options === "object" && options !== null ? options : undefined;
  if (!filterOptions?.includeAllParticipants && !isEdgeParticipant(participant)) {
    return false;
  }
  return (
    participant.upload_alert === "no_upload_24h" ||
    participant.upload_alert === "gap_1h"
  );
}

export function getUploadAlertKey(participant: Participant): string {
  const alertOccurrence =
    participant.upload_alert === "no_upload_24h"
      ? participant.last_uploaded_at ?? "never"
      : participant.capture_gap_started_at ??
        participant.upload_gap_started_at ??
        "unknown";
  return [
    participant.user_id,
    participant.upload_alert ?? "",
    alertOccurrence,
    ...(participant.capture_gap_sources ?? []),
  ].join(":");
}

export function getUploadAlertBadgeLabel(participant: Participant): string {
  if (participant.upload_alert === "no_upload_24h") return "No upload 24h";
  if (participant.upload_alert === "gap_1h") return "1h data gap";
  return "Healthy";
}

export function getUploadAlertTitle(participant: Participant): string {
  if (participant.upload_alert === "no_upload_24h") {
    return "No data upload in the last 24 hours";
  }
  if (participant.upload_alert === "gap_1h") {
    return "Detected a 1 hour captured-data gap in the last 24 hours";
  }
  return "Uploads look healthy";
}

export function getUploadAlertWindowLabel(participant: Participant): string | null {
  const startIso =
    participant.capture_gap_started_at || participant.upload_gap_started_at;
  const endIso =
    participant.capture_gap_ended_at || participant.upload_gap_ended_at;
  if (!startIso || !endIso) return null;
  const startMs = new Date(startIso).getTime();
  const endMs = new Date(endIso).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  return `${formatDateShort(startIso)} → ${formatDateShort(endIso)} MST`;
}

export function getUploadAlertDescription(participant: Participant): string {
  if (participant.upload_alert === "no_upload_24h") {
    if (participant.last_uploaded_at) {
      return `Last upload was ${relativeTime(participant.last_uploaded_at)}.`;
    }
    return "No uploads were recorded in the last 24 hours.";
  }

  if (participant.upload_alert === "gap_1h") {
    const gap = formatGapDuration(
      participant.capture_gap_duration_seconds ??
        participant.upload_gap_duration_seconds,
    );
    const sources = formatSourceList(participant.capture_gap_sources);
    return `Detected a ${gap} gap in captured data across ${sources}.`;
  }

  return "";
}

export function getUploadAlertSeverity(participant: Participant): number {
  if (participant.upload_alert === "no_upload_24h") return 0;
  if (participant.upload_alert === "gap_1h") return 1;
  return 2;
}

export function getUploadAlertReferenceTime(participant: Participant): string {
  return participant.last_uploaded_at || participant.last_seen || "";
}

export function formatGapDuration(seconds?: number): string {
  if (!seconds || seconds <= 0) return "0m";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

export function formatSourceList(sources?: string[]): string {
  if (!sources || sources.length === 0) return "periodic sources";
  return sources.map(formatSourceName).join(", ");
}

export function formatSourceName(source: string): string {
  return source
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
